import { Clipboard } from '@angular/cdk/clipboard';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { PendoService } from 'analytics-lib';
import { IDataSourcePathCommand, IFile, ILocalFile } from 'interfaces-lib';
import { IWorkflowService, WorkflowMessage } from 'interfaces-lib';
import { ErrorSeverity, MessageNotificationService } from 'message-notification-service-lib';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ContentTypeMapperService, FileExtensionMatcherService } from 'utils-service-lib';
import { StandardWorkflowService, WorkflowTaskRunner } from 'workflow-task-service-lib';
import { environment } from '../../../environments/environment';
import { MESSAGE_TYPE, NotetakerResponseTask } from '../../classes/notetaker-response-task';
import { IBowlingAlleyService } from '../../services/bowling-alley.service';
import { BullhornCardContextService } from '../../services/bullhorn-card-context.service';
import { BullhornDataService } from '../../services/bullhorn-data.service';
import { ConfigService } from '../../services/config.service';
import { NotetakerCallsService } from '../../services/notetaker-calls.service';
import { PresignedUploadService } from '../../services/presignedUpload.service';
import { QuestionPackManagerService } from '../../services/question-pack-manager.service';
import { QuestionPackService } from '../../services/questionPack.service';
import { ServiceProviderService } from '../../services/service.provider.service';
import { AIApps } from '../../types/AIApps';
import { IBullhornCardContext } from '../../types/bullhorn-card-context';
import { IBullhornEntity } from '../../types/bullhorn/bullhorn-entity';
import { BullhornEntityType } from '../../types/bullhorn/bullhorn-entity-type';
import { ICallSummary, IHighlight, IQandA } from '../../types/bullhorn/ICallSummary';
import { MeetingStatus } from '../../types/bullhorn/MeetingStatus';
import { INote } from '../../types/bullhorn/note';
import { IClientConfig } from '../../types/client-config';
import { IMessageHandlerComponent } from '../../types/component-method-message-handler';
import { INTQuestionandAnswer } from '../../types/NTQuestion';
import { INTRequest } from '../../types/NTRequest';
import { IDialogConfirm } from '../assist-manager-landing/prompt-profile/prompt-profile.component';
import { CalendarSettingsDialogComponent } from '../calendar-settings-dialog/calendar-settings-dialog.component';
import { DialogConfirmComponent } from '../dialog-confirm/dialog-confirm.component';
import { ICall, ISubtitles } from './notetaker-result/ICallInterface';
import { UploadDialogComponent } from './upload-dialog/upload-dialog.component';

class FailedToFetchCallError extends Error {
  constructor(message: string, public call: ICall) {
    super(message);
  }
}

interface ICallFilter {
  text?: string;
  date: string;
}

@Component({
  selector: 'app-notetaker-card',
  templateUrl: './notetaker-card.component.html',
  styleUrls: ['./notetaker-card.component.scss'],
  standalone: false,
})
export class NotetakerCardComponent implements IMessageHandlerComponent, OnInit {
  noteTakerRequest: INTRequest | undefined;
  // ToDO fix the duration display.
  displayedColumns: string[] = ['description', 'duration', 'date', 'noteAction', 'action'];
  protected noteActionTypes: string[] = [];
  protected noteActionColumnLabel = 'Note Action';
  dataSource: MatTableDataSource<ICall> = new MatTableDataSource<ICall>();
  selectedCall: ICall | null = null; // property will be updated to hold the details of that specific call.

  fileToUpload: any;

  protected readonly appVersion;
  protected readonly MeetingStatus = MeetingStatus;
  protected expandVersion = false;
  protected readonly environment = environment;

  protected bullhornCardContext!: IBullhornCardContext;

  protected hasRunYet = false;

  protected QandAs: INTQuestionandAnswer[] = [];

  protected fileUploaded = false;

  protected files: IFile[] = [];

  protected clientConfig!: IClientConfig;
  private FIND_VTT_SPEAKER_NAME_REGEX = /<v\s+([^>]+)>/;

  uploadableFileExtensions: string[] = ['vtt', 'mp3', 'wav'];
  uploadDialogExtFilter = this.uploadableFileExtensions.map(v => `.${v}`).join(',');
  allowedTypes: string[] = [
    'text/vtt',
    'audio/mpeg',
    'audio/wav',
  ];

  filterDateModel = '';
  public currentFilter: ICallFilter = { text: undefined, date: undefined };

  protected openInBowlingAlley = false;

  private readonly bowlingAlleyService: IBowlingAlleyService | undefined;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('clearSearch') clearSearch!: ElementRef;
  @ViewChild('searchInput') searchInput!: ElementRef;
  isUploadTriggered = false;

  constructor(
    private http: HttpClient,
    private bullhornDataService: BullhornDataService,
    private noteTakerCallsService: NotetakerCallsService,
    private questionPackService: QuestionPackService,
    private messageNotificationService: MessageNotificationService,
    private bullhornCardContextService: BullhornCardContextService,
    private presignedUploadService: PresignedUploadService,
    private clientConfigService: ConfigService,
    private dialog: MatDialog,
    private clipboard: Clipboard,
    private pendoService: PendoService,
    private route: ActivatedRoute,
    private qpm: QuestionPackManagerService,
    serviceProviderService: ServiceProviderService,
    private fileExtensionMatcherService: FileExtensionMatcherService,
    private contentTypeMapperService: ContentTypeMapperService
  ) {
    this.appVersion = environment.version;
    this.setCardContext();
    // TODO move namespace to more accessible place
    this.bowlingAlleyService = serviceProviderService.getBowlingAlleyService('bullhorn');
    const clientConfig$ = this.clientConfigService.getFullConfigForCorpId(
      AIApps.NOTETAKER,
      environment.ntApiGatewayUrl,
      environment.dtApiGatewayUrl,
      this.bullhornCardContext.corporationId
    );
    clientConfig$.subscribe({
      next: (value) => {
        this.clientConfig = value;
        if (!this.clientConfig.notetakerEnabled) {
          this.messageNotificationService.error('Client is not enabled');
          return;
        }
        this.setupCard();
      },
      // ToDo: merge with code in ai assist card
      error: (error) => {
        console.log(error);
        if (error.status && error.status === 401) {
          this.messageNotificationService.error('Client is not enabled');
        } else {
          this.messageNotificationService.error('Failed to load client config', error);
        }
      },
    });
  }

  filterSearchText(event: Event | undefined) {
    this.currentFilter.text = event ? (event.target as HTMLInputElement).value.trim().toLowerCase() : undefined;
    this.applyFilter();
  }

  filterByDate(event: MatDatepickerInputEvent<Date>) {
    this.currentFilter.date = new Date(event.value).toDateString();
    this.applyFilter();
  }

  clearDateFilter() {
    setTimeout(() => {
      this.currentFilter.date = undefined;
      this.applyFilter();
    }, 0);
    setTimeout(() => {
      this.filterDateModel = '';
    }, 500);
  }

  applyFilter() {
    this.dataSource.filter = JSON.stringify(this.currentFilter);
  }

  protected shouldShowUpload(): boolean {
    const entityType = this.bullhornCardContext.cardEntityType;
    return (
      entityType === BullhornEntityType.CANDIDATE ||
      entityType === BullhornEntityType.CLIENT_CONTACT
    );
  }

  private setupCard() {
    this.getEntityCalls();
    this.loadNoteActionTypes();
    this.qpm
      .ensurePackWithDefaultQuestions(this.clientConfig, this.bullhornCardContext.corporationId)
      .subscribe({
        next: (packId) => {
          this.noteTakerRequest = {
            ...this.noteTakerRequest!,
            questionPackId: packId,
          };
        },
        error: (err) => console.error('Failed to ensure question pack', err),
      });
  }

  ngOnInit(): void {
    this.getBHFiles();
    this.loadPendo();
  }

  // gets list of question pack(s)
  // adds new pack id if not tjhere already
  // send the payload - probably can rename this

  private async setCardContext() {
    const params = new URLSearchParams(window.location.search);
    params.set('EntityType', this.convertEntityParams(params.get('EntityType')));
    this.bullhornCardContext = this.bullhornCardContextService.getCardContext(params);

    this.bullhornCardContext.cardEntityName = await this.getEntityName(
      this.bullhornCardContext.cardEntityType,
      this.bullhornCardContext.cardEntityId
    );
  }

  private getEntityName(entityType: BullhornEntityType, entityId: number): Promise<string> {
    return new Promise((resolve) => {
      this.bullhornDataService
        .getEntityById(entityType, entityId, ['firstName', 'lastName'])
        .subscribe((entity: IBullhornEntity | any) => {
          resolve(`${entity.firstName ?? ''} ${entity.lastName ?? ''}`.trim());
        });
    });
  }

  private getEntityCalls() {
    this.noteTakerCallsService
      .getCallsByEntity(
        this.bullhornCardContext.corporationId,
        this.bullhornCardContext.cardEntityType,
        this.bullhornCardContext.cardEntityId.toString()
      )
      .pipe(
        map((calls) => {
          this.dataSource = new MatTableDataSource<ICall>(calls);
          this.dataSource.sort = this.sort;
          this.setFilterPredicate();
          this.openCallSummary(calls);
          this.loadNoteActions(calls);
        })
      )
      .subscribe();
  }

  private setFilterPredicate(): void {
    this.dataSource.filterPredicate = (data: ICall, filterString: string) => {
      const filter: ICallFilter = JSON.parse(filterString);

      let matchesText = true;
      if (filter.text?.length > 0) {
        matchesText =
          data.description.toLowerCase().includes(filter.text) ||
          data.date.toLowerCase().includes(filter.text);
      }

      let matchesDate = true;
      if (filter.date) {
        const dateOnly = new Date(data.date).toDateString();
        matchesDate = dateOnly === filter.date;
      }

      return matchesText && matchesDate;
    };
  }

  private openCallSummary(data: ICall[]) {
    this.route.queryParams.subscribe((params) => {
      const call = data.find((item) => item.callId === params['callId']);
      if (call) {
        this.openInBowlingAlley = true;
        this.getCallSummary([call]).subscribe((callsWithSummary) => {
          if (callsWithSummary.length > 0) {
            call.summary = callsWithSummary[0].summary;
            this.showCallDetails(call);
          }
        });
      }
    });
  }

  // This method is for insuring that entities within the urlSearchParams are by bullhorn convention.
  //
  private convertEntityParams(param: string | null): string {
    if (param) {
      return this.bullhornDataService.toBullhornEntity(param);
    } else {
      return '';
    }
  }

  private getAllCallSummaryFromFile(calls: ICall[]): Observable<ICall[] | null> {
    const fileCalls: ICall[] = calls
      .filter((call) => call.summaryPath?.path?.length > 0)
      .filter((call) => !call.summaryPath.path.includes(BullhornEntityType.NOTE));

    if (fileCalls.length === 0) {
      return of([]);
    }

    return forkJoin(fileCalls.map((call) => this.getCallSummaryFromFile(call))).pipe(
      catchError((failedToFetchCallError: FailedToFetchCallError) => {
        this.messageNotificationService.error(
          `Error getting summary for ${failedToFetchCallError.call.description}`,
          failedToFetchCallError
        );
        return of([]);
      })
    );
  }

  private getCallSummaryFromFile(call: ICall): Observable<ICall> {
    return this.bullhornDataService.getFileById(call.summaryPath.path).pipe(
      map((summary) => {
        call.summary = this.parseCallSummary(summary, call);
        call.summary.notes = call.summary.notes || '';
        return call;
      }),
      catchError((error) => {
        throw new FailedToFetchCallError(error.message, call);
      })
    );
  }

  private getCallSummaryFromNotes(calls: ICall[]): Observable<ICall[]> {
    const noteIds: Record<string, ICall> = calls
      .filter((call) => call.summaryPath?.path?.includes('Note'))
      .reduce((acc, call) => {
        const noteId = call.summaryPath.path[1];
        acc[noteId] = call;
        return acc;
      }, {});

    if (Object.keys(noteIds).length === 0) {
      return of([]);
    }

    // Search intentionally does not include isDeleted false, as note is purposefully hidden from user
    const query = `noteID: (${Object.keys(noteIds).join(' ')})`;
    return this.bullhornDataService.search(BullhornEntityType.NOTE, query, ['id', 'comments']).pipe(
      map((notes: any) => {
        return notes.map((note: any) => {
          const call = noteIds[note.id];
          call.summary = this.parseCallSummary(note.comments, call);
          call.summary.notes = call.summary.notes || '';
          return call;
        });
      })
    );
  }

  // TODO: This needs considering with the result page and once generated
  private getCallSummary(calls: ICall[]): Observable<ICall[]> {
    return forkJoin([
      this.getCallSummaryFromNotes(calls),
      this.getAllCallSummaryFromFile(calls),
    ]).pipe(map(([fromNotes, fromFiles]) => [...fromNotes, ...fromFiles]));
  }

  triggerFileInput(): void {
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = '';
      this.fileInput.nativeElement.click();
    } else {
      console.error('File input element not found');
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      console.log('Selected file:', file.name);
      this.openUploadDialog(file);
    }
    input.value = '';
  }

  async openUploadDialog(fileData: File | IFile): Promise<void> {
    if (fileData instanceof File) {
      this.fileToUpload = this.mapFile(fileData as File);
    } else {
      this.fileToUpload = fileData;
    }
    const typeOk = this.allowedTypes.indexOf(this.fileToUpload.type) !== -1 || this.fileToUpload.id;
    if (typeOk) {
      this.uploadDialog();
    } else {
      this.messageNotificationService.error(`'${this.fileToUpload.name}' is not a supported type`);
    }
  }

  private uploadDialog() {
    const uploadDialog = this.dialog.open(UploadDialogComponent, {
      data: {
        file: this.fileToUpload,
        NTRequest: this.noteTakerRequest,
      },
    });
    const upload$ = uploadDialog.afterClosed() as Observable<INTCloseEventData>;

    upload$.subscribe(async (closeEventData) => {
      if (!closeEventData) {
        return;
      }
      this.hasRunYet = true;
      const callToSave = await this.prepareCallForSave(
        closeEventData.call,
        MeetingStatus.IN_PROGRESS
      );
      this.addCallToDataSource(callToSave);
    });
  }

  async prepareCallForSave(call: ICall, status: MeetingStatus): Promise<ICall> {
    call.corpId = this.bullhornCardContext.corporationId;
    call.callId =
      'Call-' +
      `${this.bullhornCardContext.cardEntityType}-` +
      `${this.bullhornCardContext.cardEntityId}-` +
      `${crypto.randomUUID()}`;
    call.entityId = this.bullhornCardContext.cardEntityId.toString();
    call.status = status;
    call.callerId = await this.getEntityName(
      BullhornEntityType.CORPORATE_USER,
      this.bullhornCardContext.userId
    );
    call.attendeeEmail = await this.getAttendeeEmail();
    return call;
  }

  addCallToDataSource(call: ICall): void {
    this.dataSource.data = [...this.dataSource.data, call];
    this.noteTakerCallsService
      .saveCall({
        ...call,
        callerId: call.callerId,
        attendeeEmail: call.attendeeEmail,
      })
      .subscribe({
        next: () => {
          console.log(`Call uploaded successfully`, call.callId);
          this.sendRequest(call);
        },
        error: (error: any) => {
          this.messageNotificationService.error(
            `Error saving ${call.description} to database`,
            error,
            undefined,
            ErrorSeverity.CRITICAL
          );
          const failedCall = this.dataSource.data.find((item) => item.callId === call.callId);
          if (failedCall) {
            failedCall.status = 'FAIL';
            this.dataSource.data = [...this.dataSource.data];
          }
        },
      });
  }

  private getAttendeeEmail(): Promise<string> {
    return new Promise((resolve) => {
      this.bullhornDataService
        .getEntityById(
          this.bullhornCardContext.cardEntityType,
          this.bullhornCardContext.cardEntityId,
          ['email']
        )
        .subscribe((entity: IBullhornEntity | any) => {
          resolve(entity.email || '');
        });
    });
  }
  isRowDisabled(row: any): boolean {
    return row.status === MeetingStatus.FAIL;
  }

  onRowClick(row: any): void {
    if (
      (row.transcriptPath?.namespace === '' && row.summaryPath?.namespace === '') ||
      row.MeetingStatus === 'FAIL'
    ) {
      return;
    }
    this.showCallDetails(row);
  }
  showCallDetails(call: ICall): void {
    this.selectedCall = call;
    this.loadCallSummaryAndTranscript(call);
  }

  private loadCallSummaryAndTranscript(call: ICall): void {
    if (!call.summary && call.summaryPath) {
      this.getCallSummary([call]).subscribe((callsWithSummary) => {
        if (callsWithSummary.length > 0) {
          call.summary = callsWithSummary[0].summary;
        }
      });
    }
    this.loadTranscript(call);
  }

  private loadTranscript(call: ICall): void {
    if ((!call.transcript || call.transcript.length === 0) && call.transcriptPath) {
      this.processCallWithAiResponse(call);
    }
  }

  private processCallWithAiResponse(call: ICall): void {
    if (call.transcriptPath && !call.transcriptPath?.path?.includes(BullhornEntityType.NOTE)) {
      this.bullhornDataService.getFileById(call.transcriptPath.path).subscribe((transcript) => {
        call.transcript = this.parseResponse(transcript);
      });
    } else if (
      call.transcriptPath &&
      call.transcriptPath?.path?.includes(BullhornEntityType.NOTE)
    ) {
      this.bullhornDataService
        .getEntityById(BullhornEntityType.NOTE, Number(call.transcriptPath.path[1]), ['comments'])
        .subscribe((note) => {
          call.transcript = this.parseResponse((note as unknown as INote).comments as string);
        });
    }
  }

  protected mapFile(fileData: File): ILocalFile {
    return {
      id: '',
      type: fileData.type,
      name: fileData.name,
      dateModified: fileData.lastModified,
      fileSize: fileData.size,
      file: fileData,
    } as ILocalFile;
  }

  protected clearInput(inputElement: HTMLElement, focusInput?: boolean): void {
    if (this.dialog.openDialogs.length === 0) {
      this.filterSearchText(undefined);
      const input = inputElement as HTMLInputElement;
      input.value = '';
      if (focusInput) {
        inputElement.focus();
      }
    }
  }

  protected copyToClipboard(aString: string): void {
    this.clipboard.copy(aString);
    this.messageNotificationService.success(`Copied ${aString} to clipboard`);
  }

  sendRequest(call: ICall) {
    if (!this.noteTakerRequest) {
      return;
    }

    this.noteTakerRequest.callId = call.callId;
    this.noteTakerRequest.entity = {
      namespace: this.clientConfig.namespace ? this.clientConfig.namespace : '',
      path: [
        this.bullhornCardContext.cardEntityType,
        this.bullhornCardContext.cardEntityId.toString(),
      ],
    };

    if (this.fileToUpload?.file) {
      this.uploadLocalFile((this.fileToUpload as ILocalFile).file, call).subscribe((_) => {
        if (this.noteTakerRequest) {
          this.noteTakerRequest.file = this.fileToUpload;
          this.sendNoteTakerRequest(this, this.noteTakerRequest as INTRequest);
        }
      });
    } else {
      this.sendNoteTakerRequest(this, this.noteTakerRequest as INTRequest);
    }
  }

  onRetry(call: ICall, event: MouseEvent): void {
    event.stopPropagation();

    if (!this.noteTakerRequest) {
      return;
    }

    if (!call.transcriptPath) {
      this.messageNotificationService.error('This call cannot be retried. Please re-upload the recording.');
      return;
    }

    call.status = MeetingStatus.IN_PROGRESS;
    this.noteTakerRequest.file = call.transcriptPath;
    this.noteTakerRequest.transcribe = false;
    this.sendRequest(call);
  }

  onReupload(call: ICall, event: MouseEvent): void {
    event.stopPropagation();

    this.showConfirmationDialog(
      `Replace: ${call.description}`,
      `Do you want to replace this file ${call.description}? This will delete the existing file and replace it.`,
      () =>
        this.showConfirmationDialog(
          `Are You Sure?`,
          `You are about to replace the file ${call.description}. This action cannot be undone. Do you want to proceed?`,
          () => this.handleFileReupload(call)
        )
    );
  }

  private showConfirmationDialog(title: string, message: string, onConfirm: () => void): void {
    const dialogRef: MatDialogRef<DialogConfirmComponent> = this.dialog.open(
      DialogConfirmComponent,
      {
        maxWidth: '85vw',
        height: 'auto',
        autoFocus: true,
        data: {
          title,
          message,
          positiveText: 'Proceed',
          negativeText: 'Cancel',
        } as IDialogConfirm,
      }
    );

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        onConfirm();
      }
    });
  }

  private handleFileReupload(call: ICall): void {
    this.triggerFileInput();

    const inputElement = this.fileInput.nativeElement;
    inputElement.onchange = (fileEvent: Event) => {
      this.onFileSelected(fileEvent);

      this.uploadLocalFile(this.fileToUpload.file, call).subscribe({
        next: () => {
          console.log('New file uploaded successfully.');
          this.deleteOldCall(call);
        },
        error: (error: any) => {
          console.error('New file upload failed:', error);
          this.deleteOldCall(call);
          this.messageNotificationService.error(
            'Failed to upload the new file. The old file was not deleted.',
            undefined,
            undefined,
            ErrorSeverity.CRITICAL
          );
        },
      });
    };
  }

  private deleteOldCall(call: ICall): void {
    this.noteTakerCallsService.deleteCall(call).subscribe({
      next: () => {
        console.log(`Old file ${call.description} deleted successfully.`);
        this.removeCallFromDataSource(call);
      },
      error: (error) => {
        this.messageNotificationService.error(
          `Failed to delete ${call.description}`,
          error,
          undefined,
          ErrorSeverity.CRITICAL
        );
      },
    });
  }

  private removeCallFromDataSource(call: ICall): void {
    const callIndex = this.dataSource.data.indexOf(call);
    if (callIndex !== -1) {
      this.dataSource.data.splice(callIndex, 1);
      this.dataSource.data = [...this.dataSource.data]; // Trigger table re-render
    }
  }

  public sendNoteTakerRequest(
    parentComponentReference: IMessageHandlerComponent,
    request: INTRequest
  ) {
    const workflowService: IWorkflowService = new StandardWorkflowService(
      this.http,
      `${environment.kpWorkflowHost}`,
      environment.kpWorkflowStatus
    );
    const taskRunner = new WorkflowTaskRunner(
      workflowService,
      new NotetakerResponseTask(request, this.bullhornCardContext, parentComponentReference)
    );
    taskRunner.run().subscribe({
      error: (error) =>
        this.messageNotificationService.error('The workflow could not be executed.', error),
    });
  }

  private uploadLocalFile(file: File, call: ICall): Observable<IDataSourcePathCommand> {
    const fileParams = { path: `${this.bullhornCardContext.userId}/${file.name}` };
    return this.presignedUploadService.postFile(file, fileParams).pipe(
      tap((s3FileData) => {
        console.log(`Uploaded ${file.name} to ${s3FileData.path}`);
        this.fileUploaded = true;
        this.fileToUpload.s3 = s3FileData;
        delete this.fileToUpload.file;
      }),
      catchError((error) => this.handleUploadError(error, file, call))
    );
  }

  private handleUploadError(error: any, file: File, call: ICall) {
    console.error(`Upload failed for file ${file.name}:`, error);

    if (error.status === 413) {
      call.status = MeetingStatus.FAIL;
      this.saveCallStatus(call);
      this.messageNotificationService.error(`The file is too large to upload. Please try a smaller file.`);
    } else {
      call.status = MeetingStatus.FAIL;
      this.saveCallStatus(call);
      this.messageNotificationService.error(`An error occurred while uploading. Please try again later.`);
    }
    return throwError(() => error);
  }

  private saveCallStatus(call: ICall): void {
    this.noteTakerCallsService
      .saveCall({
        ...call,
        status: call.status as string,
      })
      .subscribe({
        next: () => console.log(`Call status updated successfully for ${call.callId}`),
        error: (saveError) => console.error(`Failed to update call status:`, saveError),
      });
  }

  private handleAiResponse(message: any) {
    if (message.type === 'AiQuestionResponse') {
      this.processAiQuestionResponse(message);
    } else {
      this.processAiTranscriptionResponse(message);
    }
  }

  private processAiQuestionResponse(message: any): void {
    console.log('message returned', message.message[0]);
    const call = message.message[0];
    const callToUpdate = this.findCallById(call.sortKey);
    if (callToUpdate) {
      callToUpdate.summaryPath = call.summaryPath;
      callToUpdate.displaySummaryPath = call.displaySummaryPath;
      callToUpdate.transcriptPath = call.transcriptPath;
      this.getCallSummary([callToUpdate]).subscribe((calls) => {
        callToUpdate.status = MeetingStatus.COMPLETE;
        this.messageNotificationService.success('Questions returned');
      });
    }
  }

  private processAiTranscriptionResponse(message: any): void {
    const call = message.message[0];
    const callToUpdate = this.findCallById(call.callId);
    if (callToUpdate) {
      callToUpdate.transcriptPath = call.transcriptPath;
      this.messageNotificationService.success('Transcription returned');
    }
  }

  private findCallById(id: string): ICall | undefined {
    return this.dataSource.data.find((call: ICall) => call.callId === id);
  }

  protected open(call: ICall, event: Event): void {
    event.stopPropagation();
    this.bowlingAlleyService?.launch(call, new URLSearchParams(window.location.search));
  }

  delete(call: ICall, event: any): void {
    event.stopPropagation();
    const dialogDelete: MatDialogRef<DialogConfirmComponent> = this.dialog.open(
      DialogConfirmComponent,
      {
        maxWidth: '85vw',
        height: 'auto',
        autoFocus: true,
        data: {
          title: `Delete: ${call.description}`,
          message: `Are you sure you want to delete ${call.description}?`,
          positiveText: 'Yes',
          negativeText: 'No',
        } as IDialogConfirm,
      }
    );

    dialogDelete.afterClosed().subscribe((takeAction: boolean) => {
      if (!takeAction) {
        return;
      }
      this.deleteCall(call);
    });
  }

  deleteCall(call: ICall) {
    this.noteTakerCallsService.deleteCall(call).subscribe({
      next: () => {
        this.messageNotificationService.success(`${call.description} deleted`);

        const callIndex = this.dataSource.data.indexOf(call);
        if (callIndex !== -1) {
          this.dataSource.data.splice(callIndex, 1);
          // Creating a new array triggers re rendering of data
          this.dataSource.data = [...this.dataSource.data];
        }
      },
      error: (error) => {
        this.messageNotificationService.error(`Error deleting ${call.description}`, error);
      },
    });
  }

  private parseResponse(response: string): ISubtitles[] {
    response = response.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    const lines = response.split('\n');
    let currentSubtitle: Partial<ISubtitles> = {};
    const transcript: ISubtitles[] = [];

    const isWebVTT = lines[0].toUpperCase().trim() === 'WEBVTT';

    if (isWebVTT) {
      for (const line of lines) {
        if (line.toUpperCase() === 'WEBVTT') {
          continue;
        }
        if (line.trim()) {
          if (line.includes('-->')) {
            currentSubtitle.time = line.trim();
          } else if (!currentSubtitle.id && !currentSubtitle.time) {
            currentSubtitle.id = line.trim();
          } else {
            const result = this.FIND_VTT_SPEAKER_NAME_REGEX.exec(line);
            currentSubtitle.name = result && result.length > 1 ? result[1] : null;
            const cleanedText = line.replace(/<[^>]+>/g, '').trim();
            currentSubtitle.text =
              (currentSubtitle.text ? currentSubtitle.text + ' ' : '') + cleanedText;
          }
        } else if (currentSubtitle.text) {
          // Line with empty string means it's the end of the transcript block
          transcript.push(currentSubtitle as ISubtitles);
          currentSubtitle = {};
        }
      }
      if (currentSubtitle.text) {
        transcript.push(currentSubtitle as ISubtitles);
      }
    } else {
      // For non-WebVTT files
      for (const line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine.length > 0) {
          currentSubtitle.text = trimmedLine;
          transcript.push(currentSubtitle as ISubtitles);
          currentSubtitle = {};
        }
      }
    }
    return transcript;
  }

  protected onNoteActionChange(call: ICall, newAction: string): void {
    const noteId = call.displaySummaryPath?.path?.[1];
    if (!noteId) {
      return;
    }
    const putData = JSON.stringify({ action: newAction });
    this.bullhornDataService.createNote(putData, noteId).subscribe({
      next: () => {
        call.noteAction = newAction;
        this.messageNotificationService.success('Note action updated');
      },
      error: (err) => this.messageNotificationService.error('Failed to update note action', err),
    });
  }

  private loadNoteActionTypes(): void {
    this.bullhornDataService.getNoteActionTypes().subscribe({
      next: ({ label, types }) => {
        this.noteActionColumnLabel = label;
        this.noteActionTypes = types;
      },
      error: (err) => console.error('Failed to load note action types', err),
    });
  }

  private loadNoteActions(calls: ICall[]): void {
    const noteIdToCall: Record<string, ICall> = {};
    calls.forEach((call) => {
      if (call.displaySummaryPath?.path?.[0] === BullhornEntityType.NOTE) {
        noteIdToCall[call.displaySummaryPath.path[1]] = call;
      }
    });
    const noteIds = Object.keys(noteIdToCall);
    if (noteIds.length === 0) {
      return;
    }
    this.bullhornDataService.getNoteActionsForIds(noteIds).subscribe({
      next: (notes) => {
        notes.forEach((note) => {
          const call = noteIdToCall[note.id.toString()];
          if (call) {
            call.noteAction = note.action;
          }
        });
        this.dataSource.data = [...this.dataSource.data];
      },
      error: (err) => console.error('Failed to load note actions', err),
    });
  }

  handleError(message: WorkflowMessage): void {
    this.hasRunYet = false;
    this.messageNotificationService.error('Error', message.message);
    console.log(`error`, message);
  }

  handleMessage(message: WorkflowMessage): void {
    console.log(`message`, message);
    if (
      message.type === MESSAGE_TYPE.AI_TRANSCRIBE_RESPONSE ||
      message.type === MESSAGE_TYPE.AI_QUESTION_RESPONSE
    ) {
      this.handleAiResponse(message);
    }
  }

  handleQuestion(message: WorkflowMessage) {}

  private getBHFiles() {
    this.setFileList();
  }

  private parseCallSummary(summary: string, call: ICall): ICallSummary {
    const summaryObject = JSON.parse(summary);

    // Map the parsed object to ICallSummary structure

    // todo move duration into adv2 - in case we add more detail for callDetails
    if (summaryObject.callDetail && summaryObject.callDetail.length > 0) {
      const durationDetail = summaryObject.callDetail.find((detail: any) => detail.answer);
      if (durationDetail) {
        call.duration = durationDetail.answer;
      }
    }
    return {
      QandAs: (summaryObject.QandAs || []).filter((qanda: IQandA | null) => qanda !== null),
      summary: {
        highlights: (summaryObject.summary?.highlights || []).map((highlight: IHighlight) => ({
          title: highlight.title,
          value: highlight.value,
        })),
        format: summaryObject.summary?.format,
      },
      notes: summaryObject.notes || '',
      callDetail: summaryObject.callDetail || [],
    } as ICallSummary;
  }

  // TODO: This is duplicated form bh-card comp. Make it a shared service(?)
  private setFileList() {
    const entity = {
      entity: {
        type: this.bullhornCardContext.cardEntityType,
        id: this.bullhornCardContext.cardEntityId.toString(),
      },
    };
    const query = `id:${this.bullhornCardContext.cardEntityId}`;
    const fields = 'fileAttachments';
    this.bullhornDataService.listFiles({ ...entity, query, fields }).subscribe((response) => {
      const allFiles: any[] = [];
      response.forEach((item) => {
        item.data.forEach((file: any) => {
          allFiles.push(file);
        });
      });
      const normalisedFiles = allFiles.map((file) => this.contentTypeMapperService.normaliseFileExtension(file));

      const acceptedFiles = normalisedFiles.filter((file) => {
        return this.fileExtensionMatcherService.matchesAllowedExtension(file, [...this.uploadableFileExtensions, '', undefined]);
      });

      this.files = acceptedFiles.map((item) => ({
        id: item.id,
        type: item.type,
        name: item.name,
        fileSize: item.fileSize,
        dateModified: item.dateModified,
        fileExtension: item.fileExtension,
        contentSubType: item.contentSubType,
      }));
    });
  }

  private loadPendo() {
    this.pendoService.initPendoScript(environment.ntBhCardPendoAPIKey);
  }

  openSettingsDialog(): void {
    const dialogRef = this.dialog.open(CalendarSettingsDialogComponent, {
      maxWidth: '650px',
      width: '600px',
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        console.log(`Calendar integration selected: ${result}`);
      }
    });
  }

  // Keyboard Shortcuts
  // - Use Alt + Shift + V to enlarge version number
  // - Use Escape to clear Search
  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    const clearSearch = this.clearSearch?.nativeElement;
    if (event.code === 'Escape' && this.clearSearch) {
      clearSearch.click();
    }

    if (event.altKey && event.shiftKey) {
      if (event.code === 'KeyV') {
        this.expandVersion = true;
        setTimeout(() => {
          this.expandVersion = false;
        }, 3000);
      }
    }
  }
}

export interface INTCloseEventData {
  call: ICall;
  action: 'upload' | null;
  NTRequest: INTRequest;
}
